import axios from 'axios';
import { CreatePurchaseRequestDto, PurchaseRequest, RequestStatus } from '../types/request.types';
import { API_CONFIG } from '../config/api';

const API_URL = API_CONFIG.BASE_URL;

// Create axios instance with auth header
const createAuthenticatedAxios = () => {
  const token = localStorage.getItem('token');
  return axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
  });
};

export const requestService = {
  async createRequest(data: CreatePurchaseRequestDto): Promise<PurchaseRequest> {
    const formData = new FormData();
    
    // Add request data
    Object.entries(data).forEach(([key, value]) => {
      if (key !== 'attachments') {
        formData.append(key, value.toString());
      }
    });

    // Add attachments
    if (data.attachments) {
      data.attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }

    const token = localStorage.getItem('token');
    const response = await axios.post<PurchaseRequest>(
      `${API_URL}/requests`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      }
    );
    return response.data;
  },

  async getRequests(): Promise<PurchaseRequest[]> {
    const api = createAuthenticatedAxios();
    const response = await api.get<PurchaseRequest[]>('/requests');
    return response.data;
  },

  async getRequestById(id: string): Promise<PurchaseRequest> {
    const api = createAuthenticatedAxios();
    const response = await api.get<PurchaseRequest>(`/requests/${id}`);
    return response.data;
  },

  async updateRequestStatus(
    id: string,
    status: RequestStatus,
    comment?: string,
    paymentType?: string,
    paymentReference?: string
  ): Promise<PurchaseRequest> {
    const api = createAuthenticatedAxios();
    const response = await api.patch<PurchaseRequest>(
      `/requests/${id}/status`,
      { status, comment, paymentType, paymentReference }
    );
    return response.data;
  },

  async addComment(id: string, content: string): Promise<PurchaseRequest> {
    const api = createAuthenticatedAxios();
    const response = await api.post<PurchaseRequest>(
      `/requests/${id}/comments`,
      { content }
    );
    return response.data;
  },

  async getRequestsByStatus(status: RequestStatus): Promise<PurchaseRequest[]> {
    const api = createAuthenticatedAxios();
    const response = await api.get<PurchaseRequest[]>(
      `/requests?status=${status}`
    );
    return response.data;
  },

  async getMyRequests(): Promise<PurchaseRequest[]> {
    const api = createAuthenticatedAxios();
    const response = await api.get<PurchaseRequest[]>(`/requests?myRequestsOnly=true`);
    return response.data;
  },

  async getDepartmentRequests(): Promise<PurchaseRequest[]> {
    const api = createAuthenticatedAxios();
    const response = await api.get<PurchaseRequest[]>(`/requests`);
    return response.data;
  },

  async submitRequest(id: string): Promise<PurchaseRequest> {
    const api = createAuthenticatedAxios();
    const response = await api.post<PurchaseRequest>(
      `/requests/${id}/submit`
    );
    return response.data;
  },

  async requestMoreInfo(
    id: string, 
    comment: string,
    paymentType?: string,
    paymentReference?: string
  ): Promise<PurchaseRequest> {
    const api = createAuthenticatedAxios();
    const response = await api.patch<PurchaseRequest>(
      `/requests/${id}/status`,
      { 
        status: 'MORE_INFO_NEEDED',
        comment,
        paymentType,
        paymentReference
      }
    );
    return response.data;
  },

  async markAsPaid(id: string, paymentType: string, paymentReference: string): Promise<PurchaseRequest> {
    const api = createAuthenticatedAxios();
    const response = await api.patch<PurchaseRequest>(`/requests/${id}/status`, {
      status: RequestStatus.PAID,
      paymentType,
      paymentReference
    });
    return response.data;
  },

  async getActionHistory(): Promise<any[]> {
    const api = createAuthenticatedAxios();
    const response = await api.get('/requests/action-history');
    return response.data;
  }
}; 